import { useState, useEffect } from 'react';
import ReactDatePicker from 'react-datepicker';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  ButtonGroup,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import { enGB } from 'date-fns/locale';
import { Global } from '@emotion/react';
import { mdiCalendar } from '@mdi/js';
import { useDrawerStateDispatch } from 'features/main/context';
import { closeDrawer } from 'features/main/context/drawer/actionCreators';
import { datePickerStyles } from 'features/usage/components/datePickerStyles';
import {
  useInsertGoLiveDateMutation,
  GetGoLiveDateDocument,
} from 'gql/graphql';
import { useFeature } from 'lib/featureFlags/features';
// import { setQAGoLiveAnchor } from 'features/common/utils/goLiveText'; // TODO: QA TESTING ONLY

interface GoLiveDatePickerProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  error?: string;
  warning?: string;
}

const GoLiveDatePicker = ({
  value,
  onChange,
  error,
  warning,
}: GoLiveDatePickerProps) => {
  const formattedDate = value ? format(value, 'MMMM do, yyyy') : '';

  return (
    <FormControl isInvalid={!!error}>
      <FormLabel>Go-live date</FormLabel>
      <Popover placement='bottom-start' isLazy>
        {({ onClose }) => (
          <>
            <PopoverTrigger>
              <InputGroup cursor='pointer'>
                <Input
                  placeholder='Select date'
                  value={formattedDate}
                  readOnly
                  cursor='pointer'
                />
                <InputRightElement pointerEvents='none'>
                  <Icon viewBox='0 0 24 24' boxSize='5' color='gray.400'>
                    <path fill='currentColor' d={mdiCalendar} />
                  </Icon>
                </InputRightElement>
              </InputGroup>
            </PopoverTrigger>
            <PopoverContent
              w='auto'
              minW='0'
              p='0'
              boxShadow='md'
              borderColor='gray.200'
              _focus={{ boxShadow: 'md' }}
            >
              <PopoverBody p='0'>
                <Global styles={datePickerStyles} />
                <ReactDatePicker
                  selected={value}
                  onChange={(date) => {
                    onChange(date);
                    onClose();
                  }}
                  inline
                  locale={enGB}
                  formatWeekDay={(day) => day.substring(0, 2).toUpperCase()}
                />
              </PopoverBody>
            </PopoverContent>
          </>
        )}
      </Popover>
      {error && <FormErrorMessage>{error}</FormErrorMessage>}
      {warning && (
        <Alert status='warning' mt='2' borderRadius='md'>
          <AlertIcon />
          <AlertDescription fontSize='sm'>{warning}</AlertDescription>
        </Alert>
      )}
    </FormControl>
  );
};

export const GoLiveDateDrawer = () => {
  const goLivePlanningEnabled = useFeature('GO_LIVE_PLANNING');
  const [{ drawerVisible, drawerId }, dispatch] = useDrawerStateDispatch();
  const isGoLiveDrawer = drawerId === 'go-live-date-drawer';
  const isOpen = goLivePlanningEnabled && drawerVisible && isGoLiveDrawer;

  useEffect(() => {
    if (!goLivePlanningEnabled && drawerVisible && isGoLiveDrawer) {
      dispatch(closeDrawer());
    }
  }, [dispatch, drawerVisible, goLivePlanningEnabled, isGoLiveDrawer]);

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [description, setDescription] = useState('');
  const [dateError, setDateError] = useState('');
  const [submitError, setSubmitError] = useState('');

  const [insertGoLiveDate, { loading }] = useInsertGoLiveDateMutation();
  const toast = useToast();

  const onClose = () => {
    dispatch(closeDrawer());
    setSelectedDate(null);
    setDescription('');
    setDateError('');
    setSubmitError('');
  };

  const onSubmit = async () => {
    if (!selectedDate) {
      setDateError('Please select a go live date.');
      return;
    }
    setDateError('');
    setSubmitError('');

    try {
      const result = await insertGoLiveDate({
        variables: {
          input: {
            goLiveDate: format(selectedDate, 'yyyy-MM-dd'),
            isLive: false,
            description: description || null,
          },
        },
        refetchQueries: [GetGoLiveDateDocument],
      });

      const payload = result.data?.insertGoLiveDate;
      if (payload?.goLiveDate) {
        // TODO: QA TESTING ONLY — reset anchor on each submission so phases restart
        // setQAGoLiveAnchor();
        onClose();
        toast({
          title: 'Go live date has been successfully updated.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        const message =
          payload?.errors?.[0]?.message ?? 'An unexpected error occurred.';
        setSubmitError(message);
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred.';
      setSubmitError(message);
    }
  };

  return (
    <Drawer onClose={onClose} isOpen={isOpen} size='md'>
      <DrawerOverlay />
      <DrawerContent data-testid='go-live-date-drawer'>
        <DrawerCloseButton
          _focus={{ outline: 'none' }}
          _focusVisible={{
            outline: '2px solid var(--colors-primary-500)',
            outlineOffset: '2px',
          }}
        />
        <DrawerHeader>Add go-live date</DrawerHeader>
        <DrawerBody>
          <VStack align='stretch' spacing='4'>
            <Alert status='info' borderRadius='md'>
              <AlertIcon />
              <AlertDescription>
                You are about to set the go-live date. If you need to update
                this date at any time, visit the{' '}
                <Link
                  color='primary.500'
                  href='https://support.sitecore.com/'
                  isExternal
                >
                  Sitecore Support Portal
                </Link>
              </AlertDescription>
            </Alert>
            <Text fontSize='sm' color='chakra-subtle-text'>
              Sharing your planned go-live date enables Sitecore Support to
              better prepare for your launch, helping us provide timely
              assistance and the right level of support when you need it most.
            </Text>
            <GoLiveDatePicker
              value={selectedDate}
              onChange={setSelectedDate}
              error={dateError}
            />
            <FormControl>
              <FormLabel>Details</FormLabel>
              <Textarea
                rows={5}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <FormHelperText>
                Add any additional notes or considerations that may help clarify
                your request
              </FormHelperText>
            </FormControl>
            {submitError && (
              <Alert status='error' borderRadius='md'>
                <AlertIcon />
                <AlertDescription fontSize='sm'>{submitError}</AlertDescription>
              </Alert>
            )}
          </VStack>
        </DrawerBody>
        <DrawerFooter justifyContent='flex-end'>
          <ButtonGroup>
            <Button variant='ghost' onClick={onClose} isDisabled={loading}>
              Cancel
            </Button>
            <Button
              colorScheme='primary'
              onClick={onSubmit}
              isLoading={loading}
            >
              Submit
            </Button>
          </ButtonGroup>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
